import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import { formatDate } from '@/core/helpers';
import { DataUnit } from '@/core/types';
import { DataCorrectionStatusEnum } from '@/core/enums';

import { ControlledCollapse } from '../Collapse/ControlledCollapse';
import { ConfrimDataUnit } from '../SellerDataUnit/ConfrimDataUnit';

import Arrow from '@/icons/select-chevron.svg?react';
import AlertCalendarIcon from '@/icons/alert-calendar.svg?react';

interface Props {
  unit: DataUnit;
  deadlineDate: Date;
  isUploadingFiles: boolean;
  onConfirm: (() => void) | undefined;
  isProcessingFiles: boolean;
  isAllDataProvided: boolean;
  hasHCE: boolean;
}

export default function ConfirmDataToDo({
  unit,
  deadlineDate,
  onConfirm,
  isUploadingFiles,
  isProcessingFiles,
  isAllDataProvided,
  hasHCE,
}: Props) {
  const { t } = useTranslation();

  const isDataBlocked =
    !!unit.confirmation_date &&
    unit.correction_status === DataCorrectionStatusEnum.Blocked;

  const isDataConfirmedByClient = isDataBlocked && unit.confirmed_by;

  return (
    <>
      {isDataBlocked ? (
        <div
          className='mt-3 rounded-lg border border-green-400 bg-[rgba(13,168,107,0.06)] p-3'
          data-testid='confirm-data-todo-tile'
        >
          <h4 className='font-bold'>
            {isDataConfirmedByClient
              ? t(
                  'Data is confirmed as ready and locked. Reports are being prepared or completed.'
                )
              : t(
                  'Data is auto-confirmed as ready and locked. Reports are being prepared or completed.'
                )}
          </h4>
          <p>
            {isDataConfirmedByClient
              ? t(
                  'You have confirmed that all data is correct and complete. The data is now locked and reports are being prepared or already completed. Any corrections will incur additional charges.'
                )
              : t(
                  'The deadline for providing and correcting data has passed; your data is confirmed and locked. Reports are being prepared or already completed. Any corrections will incur additional charges.'
                )}
          </p>
        </div>
      ) : (
        <div data-testid='confirm-data-todo-collapse'>
          <ControlledCollapse
            initialOpened={isAllDataProvided && !hasHCE}
            className={classNames('mt-2 rounded-lg border p-3', {
              'bg-gray-70': !isAllDataProvided,
              'border-[rgba(252,17,17,0.10)] bg-orange-200': isAllDataProvided,
            })}
            triggerRenderer={(isOpened, toggleCollapse) => (
              <div
                className={`flex cursor-pointer items-center justify-between gap-x-3 ${
                  isOpened ? 'rounded-t-lg' : 'rounded-lg'
                }`}
                onClick={toggleCollapse}
              >
                <div className='flex flex-grow items-center justify-start gap-x-3 font-bold'>
                  <div className='flex items-center gap-x-2'>
                    <span>
                      3.{' '}
                      {t('Confirm data readiness to proceed with VAT filing')}
                    </span>
                  </div>
                  <div>
                    <AlertCalendarIcon className='mr-1' />
                    <span className='text-orange-500'>
                      {t('by *date*').replace(
                        '*date*',
                        formatDate(deadlineDate, 'E, d MMM')
                      )}
                    </span>
                  </div>
                </div>
                <Arrow
                  className={classNames(
                    'ProvideDataToDo_Collapse SellerPastPeriodsFiling__arrow',
                    {
                      'SellerPastPeriodsFiling__arrow--active': isOpened,
                    }
                  )}
                />
              </div>
            )}
            contentRenderer={() => (
              <div className='space-y-3'>
                <p>
                  {t(
                    'Make sure all data is uploaded and all corrections are made before confirming. Once confirmed, your data will be locked, allowing us to start preparing your VAT filing. This ensures your filing is completed on time, avoiding late submission penalties.'
                  )}
                </p>
                <p>
                  <b>{t('Please note')}: </b>
                  <span>
                    {t(
                      'Changes after confirming will incur additional charges.'
                    )}
                  </span>
                </p>
                <ConfrimDataUnit
                  onConfirm={onConfirm}
                  unit={unit}
                  disabled={
                    isUploadingFiles || isProcessingFiles || !isAllDataProvided
                  }
                  styling='short'
                />
              </div>
            )}
          />
        </div>
      )}
    </>
  );
}
